/**
 * Unread-message badge service.
 *
 * Responsibilities:
 *   - Subscribe to the inbox so `conversation:unread` events fire even when
 *     the inbox view is closed (otherwise the badge never updates after the
 *     first inbox open).
 *   - Render the unread count on `#headerNotificationBadge` (and any sibling
 *     `#navNotificationBadge` if present in the drawer).
 *   - Push the count to the Android home-screen launcher badge via the
 *     `window.AppBadge` JS bridge installed by MainActivity (ShortcutBadger).
 *
 * The legacy span sits inside #homeProfileBtn and was previously hidden with
 * `display:none`. We change it to show the number when unread > 0 and stay
 * hidden otherwise.
 */

import * as bus from '../../core/eventBus.js';
import * as conversationService from './conversation.service.js';
import { createLogger } from '../../core/logger.js';

const log = createLogger('unread-badge');

const HEADER_BADGE_ID = 'headerNotificationBadge';
const NAV_BADGE_ID = 'navNotificationBadge';

let inboxUnsubscribe = /** @type {(() => void) | null} */ (null);
let initialised = false;
let lastCount = 0;

/** Wire bus listeners + start inbox subscription. Safe to call repeatedly. */
export function init() {
  if (initialised) return;
  initialised = true;
  bus.on('conversation:unread', ({ unread }) => render(unread));
  bus.on('auth:ready', () => startInboxSubscription());
  bus.on('auth:logged-out', () => {
    stopInboxSubscription();
    render(0);
  });
  // If auth was already ready before we wired up, start now.
  startInboxSubscription();
}

function startInboxSubscription() {
  stopInboxSubscription();
  try {
    inboxUnsubscribe = conversationService.subscribeInbox(() => {
      // The service emits `conversation:unread` itself; nothing to do here.
    });
  } catch (err) {
    log.warn('subscribeInbox failed:', err);
  }
}

function stopInboxSubscription() {
  if (inboxUnsubscribe) {
    try { inboxUnsubscribe(); } catch { /* ignore */ }
    inboxUnsubscribe = null;
  }
}

/**
 * @param {number} count
 */
function render(count) {
  lastCount = count;
  paintDomBadge(HEADER_BADGE_ID, count);
  paintDomBadge(NAV_BADGE_ID, count);
  // Paint any DYNAMIC badge placeholders too. Used by the home-feed messages
  // mini-card and any future surface that opts in via `data-unread-badge`.
  document.querySelectorAll('[data-unread-badge]').forEach((el) => {
    paintDynamicBadge(/** @type {HTMLElement} */ (el), count);
  });
  setLauncherBadge(count);
  if (count === 0) dismissTrayNotifications();
}

/**
 * Re-paint the badges using the last known count. Called by other views
 * (e.g. homeView) when they re-render — newly-added `[data-unread-badge]`
 * elements get the correct count without waiting for the next inbox event.
 */
export function refresh() {
  render(lastCount);
}

/** @param {HTMLElement} el @param {number} count */
function paintDynamicBadge(el, count) {
  if (count > 0) {
    el.textContent = count > 99 ? '99+' : String(count);
    el.style.display = 'inline-flex';
  } else {
    el.textContent = '';
    el.style.display = 'none';
  }
}

/**
 * @param {string} id
 * @param {number} count
 */
function paintDomBadge(id, count) {
  const el = document.getElementById(id);
  if (!el) return;
  if (count > 0) {
    el.textContent = count > 99 ? '99+' : String(count);
    el.style.display = 'flex';
  } else {
    el.textContent = '';
    el.style.display = 'none';
  }
}

/**
 * @param {number} count
 */
function setLauncherBadge(count) {
  const w = /** @type {any} */ (window);
  try {
    w.AppBadge?.set?.(count);
  } catch (err) {
    log.warn('AppBadge.set failed:', err);
  }
}

/**
 * Remove every push notification we previously posted from the system tray.
 * On Android 8+ the launcher badge often mirrors the tray, so a lingering
 * notification can leave the badge "stuck". Web build is a no-op.
 */
function dismissTrayNotifications() {
  const cap = /** @type {any} */ (window).Capacitor;
  if (!cap?.isNativePlatform?.()) return;
  const Push = cap.Plugins?.PushNotifications;
  if (!Push?.removeAllDeliveredNotifications) return;
  Push.removeAllDeliveredNotifications().catch((/** @type {Error} */ err) => {
    log.warn('removeAllDeliveredNotifications failed:', err.message);
  });
}
